import { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import { capitalizeFirstLetter } from "../utils/Date";
import {
  getMonthLabel,
  getWeekNumber,
} from "../formatted/FormattedDateAndTime";
import { useTranslation } from "react-i18next";
import { EchartsZoomFrame } from "./echartsZoom";

const HEAT_COLORS = [
  "#FCEBCF",
  "#F6D79B",
  "#E0AE5B",
  "#B77F2E",
  "#7A4E15",
  "#3D2708",
];

const hexToRgb = (hex) => {
  const value = parseInt(hex.slice(1), 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
};

const relativeLuminance = ({ r, g, b }) => {
  const toLinear = (channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

const interpolateHeatColor = (ratio) => {
  if (ratio <= 0) return HEAT_COLORS[0];
  if (ratio >= 1) return HEAT_COLORS[HEAT_COLORS.length - 1];
  const scaled = ratio * (HEAT_COLORS.length - 1);
  const index = Math.floor(scaled);
  const fraction = scaled - index;
  const start = hexToRgb(HEAT_COLORS[index]);
  const end = hexToRgb(
    HEAT_COLORS[Math.min(index + 1, HEAT_COLORS.length - 1)],
  );
  const mix = (from, to) => Math.round(from + (to - from) * fraction);
  const rgb = {
    r: mix(start.r, end.r),
    g: mix(start.g, end.g),
    b: mix(start.b, end.b),
  };
  return `#${[rgb.r, rgb.g, rgb.b].map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;
};

// Au-delà de cette luminance de fond, le texte sombre offre un meilleur
// contraste que le blanc (croisement des ratios WCAG).
const DARK_LABEL_LUMINANCE_THRESHOLD = 0.2;

const getHeatmapLabelColor = (value, maxValue) => {
  const background = interpolateHeatColor(value / maxValue);
  return relativeLuminance(hexToRgb(background)) >
    DARK_LABEL_LUMINANCE_THRESHOLD
    ? "#1F1F1F"
    : "#FFFFFF";
};

const AXIS_LEFT = 140;
const AXIS_RIGHT = 24;
const WEEK_COLUMN_WIDTH = 78;

// Nombre de colonnes de semaines qui tiennent une fois l'axe Y réservé.
const columnsFromWidth = (width) => {
  if (!width) return null;
  const available = Math.max(width - AXIS_LEFT - AXIS_RIGHT, WEEK_COLUMN_WIDTH);
  return Math.max(1, Math.floor(available / WEEK_COLUMN_WIDTH));
};

export const HeatMapChart = ({
  anomalies,
  viewMode = "day",
  currentPage,
  weeksPerPage,
  setTotalPages,
  setWeeksPageLabel,
  setCurrentPage,
}) => {
  const { t, i18n } = useTranslation();
  const chartRef = useRef(null);
  const [columnsPerPage, setColumnsPerPage] = useState(0);

  useEffect(() => {
    if (viewMode !== "week") return undefined;
    const node = chartRef.current;
    if (!node) return undefined;

    const update = () => {
      const next = columnsFromWidth(node.clientWidth) || weeksPerPage;
      if (!next) return;
      setColumnsPerPage((prev) => (prev === next ? prev : next));
    };

    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [viewMode, weeksPerPage]);

  useEffect(
    () => {
      if (
        !chartRef.current ||
        !anomalies ||
        Object.keys(anomalies).length === 0
      )
        return;
      if (viewMode === "week" && !columnsPerPage) return;
      const anomaliesChart = echarts.init(chartRef.current);

      const daysOfWeek = [
        t("Monday"),
        t("Tuesday"),
        t("Wednesday"),
        t("Thursday"),
        t("Friday"),
        t("Saturday"),
        t("Sunday"),
      ];

      const getCorrectDay = (datetime) => {
        const date = new Date(datetime);
        return daysOfWeek[(date.getDay() + 6) % 7];
      };

      const collectSortedLabels = (anomalies, mode) => {
        const sortByLabel = new Map();
        Object.values(anomalies).forEach((entries) => {
          entries.forEach(({ datetime }) => {
            const date = new Date(datetime);
            if (mode === "week") {
              const label = getWeekNumber(date, i18n.language);
              if (!sortByLabel.has(label)) {
                const match = String(label).match(/(\d+)\s+(\d{4})$/);
                const week = match ? Number(match[1]) : 0;
                const year = match ? Number(match[2]) : 0;
                sortByLabel.set(label, year * 100 + week);
              }
            }
            if (mode === "month") {
              const label = capitalizeFirstLetter(
                getMonthLabel(date, i18n.language),
              );
              if (!sortByLabel.has(label)) {
                sortByLabel.set(
                  label,
                  date.getFullYear() * 12 + date.getMonth(),
                );
              }
            }
          });
        });

        return [...sortByLabel.entries()]
          .sort((left, right) => left[1] - right[1])
          .map(([label]) => label);
      };

      const formatWeekPoint = (label) => {
        const match = String(label).match(/(\d+)\s+(\d{4})$/);
        if (!match) return label;
        return `${capitalizeFirstLetter(t("week"))} ${match[1]} (${match[2]})`;
      };

      const processAnomalies = (anomalies, mode, pageSize) => {
        const wasteCategories = Object.keys(anomalies);
        let timeLabels =
          mode === "day" ? daysOfWeek : collectSortedLabels(anomalies, mode);

        if (mode === "week" && timeLabels.length > 0) {
          const size = Math.max(
            1,
            pageSize || weeksPerPage || timeLabels.length,
          );
          const total = Math.ceil(timeLabels.length / size);
          setTotalPages(total);
          const page = Math.min(Math.max(currentPage, 0), total - 1);
          if (page !== currentPage && setCurrentPage) setCurrentPage(page);
          timeLabels = timeLabels.slice(page * size, page * size + size);

          const first = timeLabels[0];
          const last = timeLabels[timeLabels.length - 1];
          if (first && last) {
            setWeeksPageLabel(
              `${formatWeekPoint(first)} ${t("au")} ${formatWeekPoint(last)}`,
            );
          }
        }

        const result = [];
        wasteCategories.forEach((category, yIndex) => {
          timeLabels.forEach((label, xIndex) => {
            const filteredData = anomalies[category].filter((item) => {
              const date = new Date(item.datetime);
              if (mode === "day") return getCorrectDay(date) === label;
              if (mode === "week")
                return getWeekNumber(date, i18n.language) === label;
              if (mode === "month")
                return (
                  capitalizeFirstLetter(getMonthLabel(date, i18n.language)) ===
                  capitalizeFirstLetter(label)
                );
              return false;
            });

            const total = filteredData.reduce(
              (sum, entry) =>
                sum + Object.values(entry.total).reduce((a, b) => a + b, 0),
              0,
            );

            const count = filteredData.length;
            let value = 0;
            if (viewMode === "day") {
              value = count > 0 ? Math.round(total / count) : 0;
            } else {
              value = total;
            }
            result.push([xIndex, yIndex, value]);
          });
        });

        return { wasteCategories, timeLabels, result };
      };

      const pageSize = viewMode === "week" ? columnsPerPage : null;

      const { wasteCategories, timeLabels, result } = processAnomalies(
        anomalies,
        viewMode,
        pageSize,
      );

      const maxValue = Math.max(...result.map((d) => d[2]), 1);
      const heatmapData = result.map(([x, y, value]) => ({
        value: [x, y, value],
        label: {
          show: true,
          color: getHeatmapLabelColor(value, maxValue),
        },
      }));

      const option = {
        tooltip: { position: "top" },
        grid: {
          height: "70%",
          top: "12%",
          left: viewMode === "day" ? "20%" : AXIS_LEFT,
          right: viewMode === "day" ? "4%" : AXIS_RIGHT,
        },
        xAxis: {
          type: "category",
          position: "top",
          data: timeLabels,
          splitArea: { show: false },
          splitLine: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: "#8A8A8A",
            fontSize: 12,
            interval: 0,
            hideOverlap: false,
          },
        },
        yAxis: {
          type: "category",
          data: wasteCategories,
          splitArea: { show: false },
          splitLine: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            show: true,
            interval: 0,
            color: "#3F3F3F",
            fontSize: 13,
            formatter: function (value) {
              return value.length > 14 ? value.substring(0, 14) + "..." : value;
            },
            width: 100,
            overflow: "truncate",
          },
        },
        visualMap: {
          show: false,
          min: 0,
          max: maxValue,
          calculable: true,
          inRange: { color: HEAT_COLORS },
        },
        series: [
          {
            type: "heatmap",
            data: heatmapData,
            label: {
              show: true,
              fontSize: 12,
              formatter: (params) => String(params.value?.[2] ?? 0),
            },
            itemStyle: {
              borderColor: "#FFFFFF",
              borderWidth: 4,
              borderRadius: 8,
            },
            emphasis: {
              itemStyle: { shadowBlur: 10, shadowColor: "rgba(0, 0, 0, 0.2)" },
            },
          },
        ],
      };

      anomaliesChart.setOption(option);
      anomaliesChart.resize();

      return () => {
        anomaliesChart.dispose();
      };
    },
    // eslint-disable-next-line
    [anomalies, viewMode, currentPage, columnsPerPage, i18n.language],
  );

  return <EchartsZoomFrame chartRef={chartRef} height="100%" width="100%" />;
};
